import {
  type Dimensions,
  type NodeChange,
  useReactFlow,
  type XYPosition,
} from '@xyflow/react';
import { useMemo, useRef, useState } from 'react';

import {
  type DiagramEditorTool,
  type DiagramNodeTool,
  isNodeTool,
  NEW_NODE_BY_TOOL,
} from '../components/DiagramEditor/diagramEditorTools';
import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { addDiagramNode } from '../utils/diagramEdits';
import {
  createReactFlowDiagram,
  type ReactFlowDiagramNode,
} from '../utils/reactFlowDiagram';

/** The React Flow node ID of the ghost. No diagram node has this ID. */
export const PLACEMENT_GHOST_NODE_ID = 'placement-ghost';

/** The opacity of the ghost, so the user can tell it from a placed node. */
const PLACEMENT_GHOST_OPACITY = 0.5;

/** The ghost draws above the tiles and the stage bands. */
const PLACEMENT_GHOST_Z_INDEX = 1000;

/**
 * Returns the faded copy of the node that a click with a node placement tool
 * adds. The ghost is centered on the pointer. It is a React Flow node,
 * so it draws in canvas coordinates and scales with the zoom.
 * It does not get pointer events, so a click goes to the
 * empty canvas and places the node.
 *
 * The ghost is null while the select or line tool is active, and while the
 * pointer is not on the canvas. Give the canvas pointer events and viewport
 * moves to the returned handlers. Give each node change to `onNodesChange`,
 * which returns the changes that are not for the ghost.
 */
export function useDiagramPlacementGhost(
  diagram: DiagramBuilder_DiagramFragment,
  tool: DiagramEditorTool
) {
  const { screenToFlowPosition } = useReactFlow();
  // The canvas position of the pointer.
  const [pointer, setPointer] = useState<XYPosition | null>(null);
  // React Flow measures the ghost once. Each pointer move makes a new ghost,
  // so the ghost keeps the measured size. Otherwise React Flow hides it
  // until it measures it again.
  const [measured, setMeasured] = useState<{
    tool: DiagramNodeTool;
    dimensions: Dimensions;
  } | null>(null);
  const latestClient = useRef<XYPosition | null>(null);
  const nodeTool = isNodeTool(tool) ? tool : null;

  // The ghost is the node that `placeElement` adds for the tool at the
  // pointer: the same kind, text, style, default size, and rounded position.
  const ghost = useMemo((): ReactFlowDiagramNode | null => {
    if (!nodeTool || !pointer) return null;
    const added = addDiagramNode(
      { ...diagram, content: { ...diagram.content, nodes: [], edges: [] } },
      PLACEMENT_GHOST_NODE_ID,
      pointer,
      NEW_NODE_BY_TOOL[nodeTool]
    );
    const [node] = createReactFlowDiagram(added).nodes;
    if (!node) return null;
    return {
      ...node,
      origin: [0.5, 0.5],
      measured: measured?.tool === nodeTool ? measured.dimensions : undefined,
      selectable: false,
      draggable: false,
      connectable: false,
      focusable: false,
      deletable: false,
      zIndex: PLACEMENT_GHOST_Z_INDEX,
      style: { opacity: PLACEMENT_GHOST_OPACITY, pointerEvents: 'none' },
    };
  }, [diagram, nodeTool, pointer, measured]);

  const moveTo = (client: XYPosition) => {
    latestClient.current = client;
    setPointer(screenToFlowPosition(client));
  };

  return {
    ghost,
    /** Saved nodes use a top-left origin, unlike the centered ghost. */
    getPlacementPosition: (position: XYPosition): XYPosition => ({
      x: position.x - (ghost?.measured?.width ?? ghost?.width ?? 0) / 2,
      y: position.y - (ghost?.measured?.height ?? ghost?.height ?? 0) / 2,
    }),
    onPaneMouseMove: (event: { clientX: number; clientY: number }) =>
      moveTo({ x: event.clientX, y: event.clientY }),
    onPaneMouseLeave: () => {
      latestClient.current = null;
      setPointer(null);
    },
    /** A pan or zoom moves the canvas under a pointer that does not move. */
    onMove: () => {
      if (latestClient.current) moveTo(latestClient.current);
    },
    onNodesChange: <T extends NodeChange>(changes: T[]): T[] => {
      const size = changes.find(
        (change) =>
          change.type === 'dimensions' &&
          change.id === PLACEMENT_GHOST_NODE_ID &&
          change.dimensions
      );
      if (size?.type === 'dimensions' && size.dimensions && nodeTool) {
        setMeasured({ tool: nodeTool, dimensions: size.dimensions });
      }
      return changes.filter(
        (change) => !('id' in change && change.id === PLACEMENT_GHOST_NODE_ID)
      );
    },
  };
}
